import {action, autorun, computed, observable, runInAction, toJS} from 'mobx';
import {actions, category, events, labels, PaymentErrors, PaymentStatus, PaymentType, SubscriptionPlans} from '../../../constants/enums';
import {DEFAULT_SUBSCRIPTION_PLAN, monthlyReferrerCoupon, PAYMENT_NAV, SUBSCRIPTION_PLANS} from '../../../constants/constants';
import {go} from '../../../utils/navigationUtility';
import {
    getId,
    getPaymentStatus,
    getTotalAmount,
    getUserPaymentType,
    getUserPlan,
    getUserPlanForAnalytics,
    getUserReferrer,
    isActiveUserPlan
} from '../../../utils/userUtility';
import userStore from '../../../stores/UserStore';
import manageStore from '../../../stores/ManageStore';
import request from "../../../utils/request";
import {CANCEL_SUBSCRIPTION, PAYMENT} from "../../../constants/urlConstants";
import {customEvent} from "../../../utils/analyticsUtil";
import qs from 'query-string';
import isEmpty from "lodash/isEmpty";

const testParam = process.env.REACT_APP_PAYMENT_TEST === 'true';
const secretKey = process.env.REACT_APP_PAYMENT_TEST_SECRET;

class PaymentStore {
    @observable subscriptionType = PaymentType.Yearly;
    @observable nav = PAYMENT_NAV;
    @observable paymentError = null;
    @observable calculating = true;
    @observable calculation = {};
    @observable isPaymentSuccessMessage = false;
    @observable firstTimePayment = true;
    @observable unauthorizedFlow = false;
    skipTrial = true;
    coupon = null;
    referralCouponActive = false;

    @observable customProductId = null;
    customTemplateId = null;
    @observable customPrice = null;


    constructor() {
        autorun(() => {
            if (userStore.isAuthenticated) {
                runInAction(() => {
                    switch (getUserPlan()) {
                        case SubscriptionPlans.Free:
                        case SubscriptionPlans.Trial:
                            this.firstTimePayment = true;
                            break;
                        case SubscriptionPlans.Basic:
                        case SubscriptionPlans.Academy:
                        case SubscriptionPlans.Conservatory:
                        case SubscriptionPlans.School:
                        case SubscriptionPlans.Custom:
                        case SubscriptionPlans.Unlimited:
                        default:
                            this.firstTimePayment = getPaymentStatus() !== PaymentStatus.Active;
                    }

                    this.referralCouponActive = getUserReferrer() && this.firstTimePayment;
                });
            }
        })
    }

    @computed get disabledMonthly() {
        return (getPaymentStatus() === PaymentStatus.Active && getUserPlan() === SubscriptionPlans.Unlimited && getUserPaymentType() === PaymentType.Monthly) || getUserPaymentType() === PaymentType.Yearly;
    }

    @computed get disabledYearly() {
        return getUserPlan() === SubscriptionPlans.Unlimited && getUserPaymentType() === PaymentType.Yearly;
    }

    @computed
    get iframeSrc() {
        let productId = this.customProductId || this.activePlan.productId[this.subscriptionType];
        const templateId = this.customTemplateId || 15486;
        // const quantity = this.subscriptionStudents;
        // const unitPrice = this.planUnitPrice;
        const thankYouButton = this.unauthorizedFlow ? 'Continue to Musico' : 'Go to Students List';
        let coupon = this.coupon;
        if (!coupon) {
            if (this.referralCouponActive) {
                coupon = this.activePlan.referrerCoupon[this.subscriptionType] || monthlyReferrerCoupon;
            } else {
                // use this for default coupon
                // coupon = birthdayCoupon;
            }
        }
        let params = {
            'products[1][id]': productId,
            'page-template': templateId,
            'use-test-mode': testParam,
            'secret-key': secretKey ? secretKey : undefined,
            'exfo': '742',
            'coupon-code-to-add': coupon,
            'x-uid': getId(),
            'x-type:': this.subscriptionType,
            'x-plan-key': this.activePlan.key,
            'x-thankyou-button': thankYouButton,
            'x-coupon': coupon
        }
        return `https://store.payproglobal.com/checkout?${qs.stringify(params)}`
    }

    @computed
    get activeStep() {
        return this.nav.find(i => i.active);
    }

    @computed
    get activePlan() {
        if (!isEmpty(toJS(this.customProductId))) {
            return SUBSCRIPTION_PLANS[6]; // custom
        }
        return DEFAULT_SUBSCRIPTION_PLAN;
    }

    setSkipTrial(trial) {
        this.skipTrial = !(trial === 'true');
    }

    setCoupon(coupon) {
        this.coupon = coupon;
    }

    @action setCustomProduct(productId, templateId, price) {
        this.customProductId = productId;
        this.customTemplateId = templateId;
        this.customPrice = price;
    }


    @computed get planDetails() {
        return {
            planType: this.activePlan.key,
            paymentType: this.subscriptionType,
        };
    }

    @computed
    get discount() {
        return 100 - this.activePlan.yearly / this.activePlan.monthly * 100;
    }

    @computed
    get noPricePlan() {
        return [SubscriptionPlans.Free, SubscriptionPlans.School].includes(this.activePlan.key) && (!isActiveUserPlan() || getUserPlan() === SubscriptionPlans.Custom);
    }

    @action setSubscriptionType = (val) => {
        this.subscriptionType = val;
    };

    @action resetNav = () => {
        this.nav = PAYMENT_NAV;
    };

    @action goToStep = (key) => {
        // if (key === PaymentSteps.Plan) {
        go('/payment');
        // }
        this.nav = this.nav.map(i => ({...i, disable: false, active: i.key === key}))
    };

    @action setIsPaymentSuccessMessage = (val) => {
        this.isPaymentSuccessMessage = val;
    };

    @action paymentConfirmed = () => {
        customEvent(events.CustomEvent, {category: this.firstTimePayment? category.Subscribe : category.ChangePlan,
            action: this.subscriptionType,
            label: labels.Success});
        let currentTotalAmount = getTotalAmount();
        let maxTimes = 60;
        let counter = 0;
        let interval = setInterval(async () => {
            await userStore.updateUserPaymentStatus();
            counter++;
            if (currentTotalAmount !== getTotalAmount() || counter > maxTimes) {
                clearInterval(interval);
                runInAction(() => {
                    this.referralCouponActive = false;
                })
                customEvent(events.UpdatePaymentStatus, {plan: getUserPlanForAnalytics()});
                customEvent(events.CustomEvent, {category: category.Pricing, action: actions.PurchaseConfirmed, label: this.planDetails});
                manageStore.fetchStudents();
            }
        }, 1000)
    };

    @action changePlan = async () => {
        try {
            this.setPaymentError(null);
            const result = await request(`${PAYMENT}/change`, {
                method: 'POST',
                body: {
                    planType: this.activePlan.key,
                    type: this.subscriptionType
                }
            }, true);
            if (result.success) {
                this.paymentConfirmed();
                return;
            }
            throw new Error("paypro error", result.err);
        } catch (err) {
            this.setPaymentError(PaymentErrors.SomethingWrong);
            console.warn(err);
        }
    };

    @action changePlanAdmin = async (recurringChange) => {
        try {
            this.setPaymentError(null);
            const result = await request(`${PAYMENT}/changeSubscription`, {
                method: 'POST',
                body: recurringChange
            }, true);
            if (result.success) {
                console.log(result);
                return;
            }
            throw new Error("paypro error", result.err);
        } catch (err) {
            this.setPaymentError(PaymentErrors.SomethingWrong);
            console.warn(err);
        }
    };

    @action applyOneTimeChangeAdmin = async (oneTimeCharge) => {
        try {
            this.setPaymentError(null);
            const result = await request(`${PAYMENT}/makeOnetimeCharge`, {
                method: 'POST',
                body: oneTimeCharge
            }, true);
            if (result.success) {
                console.log(result);
                return;
            }
            throw new Error("paypro error", result.err);
        } catch (err) {
            this.setPaymentError(PaymentErrors.SomethingWrong);
            console.warn(err);
        }
    };

    @action applyNextPaymentChangeAdmin = async (nextPayment) => {
        try {
            this.setPaymentError(null);
            const result = await request(`${PAYMENT}/changeNextPayment`, {
                method: 'POST',
                body: nextPayment
            }, true);
            if (result.success) {
                console.log(result);
                return;
            }
            throw new Error("paypro error", result.err);
        } catch (err) {
            this.setPaymentError(PaymentErrors.SomethingWrong);
            console.warn(err);
        }
    };


    @action setPaymentError = (val) => {
        console.log('payment error called: ' + val);
        val && customEvent(events.CustomEvent, {category: category.ChangePlan, action: this.subscriptionType, label: labels.Error});
        this.paymentError = val;
    };

    @action setUnauthorizedFlow = (val) => {
        this.unauthorizedFlow = val;
    }

    @action cancelSubscription = async () => {
        const cancelConfirm = await request(CANCEL_SUBSCRIPTION, {method: 'POST', body: {}}, true)
        return cancelConfirm?.success
    }
}


const paymentStore = new PaymentStore();
export default paymentStore;
